import type { ChatTurn, ErrorKind, WidgetConfig } from './model';
import { renderMarkdown } from './markdown';
import { describeTool } from './openwebui';
import { hasContent, mapContents } from './extract';
import { hintMoreBelow } from './scroll-hint';

export interface ChatCallbacks {
  onSend: (text: string) => void;
  /** `showMap`: the answer's map badge was pressed ("… auf der Karte zeigen"), not just the answer */
  onSelect: (turnId: string, showMap: boolean) => void;
  /** "Nochmal fragen" in the error turn `turnId` */
  onRetry: (turnId: string) => void;
}

export type ChatTexts = Pick<WidgetConfig, 'title' | 'placeholder' | 'placeholderShort' | 'aiNotice' | 'aiNoticeShort' | 'aiNoticeHref' | 'examples'>;

/** What the visitor reads when a question got no answer — the technical detail stays in the console
 *  and the turn's `data-detail`. One voice: the service is „der Assistent“, a dash is a spaced en dash. */
const ERROR_TEXT: Record<ErrorKind, string> = {
  network: 'Keine Verbindung zum Assistenten – bitte gleich nochmal versuchen.',
  server: 'Der Assistent antwortet gerade nicht – bitte gleich nochmal versuchen.',
  // 401/403/404: the key or the path is not accepted — a matter of the site's setup, not of the moment
  unavailable: 'Der Assistent ist auf dieser Seite nicht richtig eingerichtet – bitte später nochmal versuchen.',
  busy: 'Gerade sind viele Anfragen unterwegs – bitte gleich nochmal.',
  refused: 'Der Assistent konnte die Frage nicht bearbeiten – bitte gleich nochmal versuchen.',
  timeout: 'Die Antwort hat zu lange gedauert – bitte nochmal fragen, gern auch in kleineren Schritten.',
  unexpected: 'Die Antwort ließ sich nicht lesen – bitte gleich nochmal versuchen.',
  setup: 'Der Assistent ist auf dieser Seite noch nicht eingerichtet.',
};

export const errorText = (kind: ErrorKind): string => ERROR_TEXT[kind];

/** `href` as an absolute http(s) address (a relative one resolved against `base`), or null: the
 *  address may come from a URL fragment (embed.html), so no `javascript:` or `data:` link. */
export function webLink(href: string, base: string): string | null {
  try {
    const url = new URL(href.trim(), base);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

/** The example questions of the attribute `examples`, separated by „|“: no attribute gives the
 *  defaults, an empty one none. */
export function parseExamples(value: string | null, fallback: string[]): string[] {
  if (value === null) return fallback;
  return value.split('|').map((q) => q.trim()).filter(Boolean);
}

/** Asking the same question again cannot help when the widget has no key or the assistant turns it
 *  away (key or path not accepted); then no „Nochmal fragen“ is offered. */
export const askingAgainHelps = (kind: ErrorKind): boolean => kind !== 'setup' && kind !== 'unavailable';

/** "Route und 2 Orte auf der Karte zeigen" — the answer's badge names what the map will show, with the
 *  verb of what pressing it does. */
export function mapBadgeLabel({ route, tracks, places }: { route: boolean; tracks: number; places: number }): string {
  const parts = [
    route ? 'Route' : '',
    tracks === 1 ? 'Strecke' : tracks > 1 ? `${tracks} Strecken` : '',
    places === 1 ? '1 Ort' : places > 1 ? `${places} Orte` : '',
  ].filter(Boolean);
  const named = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1]}` : parts[0];
  return `${named} auf der Karte zeigen`;
}

const MAX_INPUT_HEIGHT = 140;
/** What the status line says when a question goes out — it also resets the line, so the answer that
 *  follows is a change even when it begins like the one before — and once after a long search. */
const SAID_ASKED = 'Frage gesendet – ich suche die Antwort …';
const SAID_STILL = 'Noch dabei …';
/** the input's placeholder while a question is searched: the input takes nothing then; it says what
 *  the status line says */
const WAITING_PLACEHOLDER = 'Ich suche die Antwort …';
const STILL_AFTER_MS = 15_000;
/** A long answer opens with its question whole above it when the question and this many lines of the
 *  answer fit the history; otherwise the question is just out of sight. */
const ANSWER_LINES_UNDER_QUESTION = 3;
/** how far the history's top fade ramps from transparent to opaque (styles.css: 8 px to 16 px) */
const FADE_RAMP_PX = 8;

const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** What the history keeps in sight while its content and its box change size: the newest turn, the
 *  start of a finished answer taller than the box — or, once the visitor scrolled elsewhere, nothing. */
type Anchor = { to: 'end' } | { to: 'start'; id: string } | null;

export class ChatView {
  readonly root: HTMLElement;
  private readonly list: HTMLElement;
  private readonly form: HTMLFormElement;
  private readonly input: HTMLTextAreaElement;
  private readonly send: HTMLButtonElement;
  private readonly intro: HTMLElement;
  private readonly examples: HTMLElement;
  /** the questions the example buttons were built from */
  private examplesShown: string[] = [];
  /** The polite status line: the question went out, still searching, the answer is there. It lives
   *  outside `root` (the widget places it), so a sheet that makes the chat inert does not silence it. */
  readonly status: HTMLElement;
  private busy = false;
  /** the kind of pointer that last pressed something in the chat (`PointerEvent.pointerType`) */
  private pointer = '';
  private stillTimer: ReturnType<typeof setTimeout> | undefined;
  private selected: string | null = null;
  private turns: ChatTurn[] = [];
  /** the newest turn at the last render (the widget hands in the same array, grown in place) */
  private lastTurnId: string | undefined;
  /** each turn's element and the markup it was built from: only a turn whose markup changed is replaced */
  private readonly drawn = new Map<string, { html: string; el: HTMLElement }>();
  private anchor: Anchor = { to: 'end' };
  /** where the widget itself last scrolled the history to */
  private ownScrollTop = -1;
  // the box (the widget resized, the map hidden) and every turn (a font arriving re-wraps the text)
  private readonly keepInSight = new ResizeObserver(() => this.keepAnchor());
  private readonly fitPlaceholder = new ResizeObserver(() => this.refreshTexts());

  /** `texts` is read again on `refreshTexts`: title, placeholder and the AI notice may change after mount */
  constructor(private readonly cb: ChatCallbacks, private readonly texts: () => ChatTexts) {
    this.root = document.createElement('section');
    this.root.className = 'chat';
    // The AI notice comes in two forms; styles.css shows the short one in a chat up to 440 px, where
    // the footer under the input goes. The hidden form is display: none, out of the accessibility tree.
    // The short form fills a phone's line by itself, so with an address it is the link (its name ends
    // in „Mehr dazu“); without one its <a> has no href and is plain text. The long form has „Mehr dazu“ after it,
    // and stands on a phone too when there is no short form (`has-short`), never a link over several lines.
    this.root.innerHTML = `
      <header class="chat-head">
        <div class="chat-head-row"><h2 class="chat-title"></h2></div>
        <p class="chat-sub"></p>
        <p class="ai-notice" role="note"><span class="ai-notice-long"></span><a class="ai-notice-short" target="_blank" rel="noopener"><span class="ai-notice-short-text"></span><span class="visually-hidden"> Mehr dazu</span></a> <a class="ai-notice-more" target="_blank" rel="noopener">Mehr dazu</a></p>
      </header>
      <div class="examples" hidden>
        <p class="examples-title" id="nds-examples-title">Zum Beispiel</p>
        <ul class="examples-list" aria-labelledby="nds-examples-title"></ul>
      </div>
      <ol class="turns" aria-label="Verlauf" tabindex="0"></ol>
      <form class="ask">
        <label class="visually-hidden" for="nds-ask">Deine Frage</label>
        <textarea id="nds-ask" rows="1" autocomplete="off"></textarea>
        <button type="submit" class="send" aria-label="Senden">${SEND_ICON}</button>
      </form>
      <p class="ai-notice ai-notice-foot">Antworten sind mit KI erstellt und automatisch generiert.</p>`;
    this.list = this.root.querySelector('.turns')!;
    this.form = this.root.querySelector('form')!;
    this.input = this.root.querySelector('textarea')!;
    this.send = this.root.querySelector('button.send')!;
    this.intro = this.root.querySelector('.chat-sub')!;
    this.examples = this.root.querySelector('.examples')!;
    // a low history cuts the list: its last line fades out until it is scrolled to its end
    hintMoreBelow(this.examples);
    this.status = document.createElement('p');
    this.status.className = 'visually-hidden answer-status';
    this.status.setAttribute('role', 'status');
    this.refreshTexts();
    this.describeMap(true, true);
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submit();
    });
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.submit();
      }
    });
    this.input.addEventListener('input', () => this.autosize());
    this.list.addEventListener('click', (e) => this.onListClick(e));
    this.examples.addEventListener('click', (e) => this.onExampleClick(e));
    // whether the visitor works the chat by touch: the focus then keeps off the input (focusForm)
    this.root.addEventListener('pointerdown', (e) => (this.pointer = e.pointerType), { capture: true });
    // the event of our own scroll arrives a frame late, when a font may already have re-wrapped the
    // turns: the position we scrolled to keeps the anchor
    this.list.addEventListener('scroll', () => {
      const { scrollHeight, scrollTop, clientHeight } = this.list;
      if (scrollTop === this.ownScrollTop && this.anchor) return;
      this.anchor = scrollHeight - scrollTop - clientHeight < 80 ? { to: 'end' } : null;
      this.fadeQuestionOverError();
    });
    this.keepInSight.observe(this.list);
    this.fitPlaceholder.observe(this.root);
    // the placeholder is measured in the widget's font, which may arrive after the first measurement
    document.fonts.addEventListener('loadingdone', () => this.refreshTexts());
  }

  /** Put a control (the map toggle) at the end of the title row, and its status line below. */
  addHeaderAction(action: HTMLElement, status: HTMLElement): void {
    this.root.querySelector('.chat-head-row')!.appendChild(action);
    this.root.querySelector('.chat-head')!.appendChild(status);
  }

  /** The title, the AI notice, the example questions, and the placeholder: while a question is
   *  searched the waiting text, else for the input's width the long one where it fits whole, else the
   *  short one. */
  refreshTexts(): void {
    const { title, placeholder, placeholderShort, aiNotice, aiNoticeShort, aiNoticeHref, examples } = this.texts();
    setText(this.root.querySelector('.chat-title')!, title);
    const notice = this.root.querySelector<HTMLElement>('.chat-head .ai-notice')!;
    // an empty `ai-notice` switches the notice off, in the head and under the input
    notice.hidden = !aiNotice;
    // a host's own notice without a short form arrives as both forms (widget.ts)
    notice.classList.toggle('has-short', aiNoticeShort !== aiNotice);
    this.root.querySelector<HTMLElement>('.ai-notice-foot')!.hidden = !aiNotice;
    setText(notice.querySelector('.ai-notice-long')!, aiNotice);
    setText(notice.querySelector('.ai-notice-short-text')!, aiNoticeShort);
    const href = aiNoticeHref ? webLink(aiNoticeHref, document.baseURI) : null;
    const short = notice.querySelector<HTMLAnchorElement>('a.ai-notice-short')!;
    const more = notice.querySelector<HTMLAnchorElement>('a.ai-notice-more')!;
    short.querySelector<HTMLElement>('.visually-hidden')!.hidden = !href;
    more.hidden = !href;
    for (const link of [short, more]) {
      if (href) link.href = href;
      else link.removeAttribute('href');
    }
    this.showExamples(examples);
    const fitting = this.busy ? WAITING_PLACEHOLDER : placeholder === placeholderShort || this.fitsInput(placeholder) ? placeholder : placeholderShort;
    if (this.input.placeholder !== fitting) this.input.placeholder = fitting;
  }

  /** One button per example question, built anew only when the questions change (a focused button
   *  stays); the list is shown while the history is empty (`placeExamples`). */
  private showExamples(examples: string[]): void {
    if (examples.join('|') !== this.examplesShown.join('|')) {
      this.examplesShown = examples;
      this.examples.querySelector('ul')!.innerHTML = examples.map((q) => `<li><button type="button" class="example">${esc(q)}</button></li>`).join('');
    }
    this.placeExamples();
  }

  /** The examples are a way into an empty history and take its place; with the first turn they make way. */
  private placeExamples(): void {
    this.examples.hidden = this.turns.length > 0 || this.examplesShown.length === 0;
    this.list.hidden = !this.examples.hidden;
  }

  /** Whether `text` fits the input's content box in one line, measured in the input's font. */
  private fitsInput(text: string): boolean {
    const style = getComputedStyle(this.input);
    const room = this.input.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    // not laid out (the widget is hidden): measured again when it gets a size
    if (room <= 0) return false;
    return textWidth(text, `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`) <= room;
  }

  /** The introduction says what the map does right now: shows what fits, can show it, or is not there. */
  describeMap(shown: boolean, canShow: boolean): void {
    const map = shown ? ' – was passt, erscheint auf der Karte' : canShow ? ' – was passt, lässt sich auf der Karte zeigen' : '';
    this.intro.textContent = `Fragen zu Verbindungen, Abfahrten und Sehenswertem in Niedersachsen${map}.`;
  }

  /** Scroll the input back into sight while it has the focus (the on-screen keyboard took the room). */
  keepInputInView(): void {
    if ((this.root.getRootNode() as Document | ShadowRoot).activeElement === this.input) this.input.scrollIntoView({ block: 'nearest' });
  }

  private submit(): void {
    const text = this.input.value.trim();
    if (!text || this.busy) return;
    this.input.value = '';
    this.autosize();
    this.cb.onSend(text);
  }

  private onListClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const turn = target.closest<HTMLElement>('.turn-assistant');
    if (!turn || target.closest('a')) return;
    if (target.closest('button.retry')) {
      // the question goes again: out of the input, where it waited to be edited
      const question = this.turns.find((t) => t.id === turn.dataset.id)?.error?.question;
      if (question && this.input.value.trim() === question) {
        this.input.value = '';
        this.autosize();
      }
      // the focus goes to the form before the button goes with its bubble, or it would fall to the page
      this.focusForm(e.detail === 0);
      this.cb.onRetry(turn.dataset.id!);
      return;
    }
    // a click anywhere on an answer selects it; for keyboard and screen readers the map badge is the button
    this.cb.onSelect(turn.dataset.id!, !!target.closest('button.map-badge'));
  }

  private onExampleClick(e: MouseEvent): void {
    const button = (e.target as HTMLElement).closest<HTMLButtonElement>('button.example');
    if (!button || this.busy) return;
    // as with „Nochmal fragen“: the list goes with the first turn, the focus must not go with it
    this.focusForm(e.detail === 0);
    this.cb.onSend(button.textContent!);
  }

  /** Put the focus into the question form: into the input — or, when the visitor works the chat by
   *  touch or pen, onto the send button, where it raises no on-screen keyboard over the answer.
   *  `byKeyboard`: the action came from a key (a click with `detail` 0), whatever pressed before. */
  private focusForm(byKeyboard = false): void {
    const onScreenKeyboard = !byKeyboard && (this.pointer === 'touch' || this.pointer === 'pen');
    (onScreenKeyboard ? this.send : this.input).focus();
  }

  /** After a failed question: put it back into the input, unless the visitor typed something else. */
  offerAgain(question: string): void {
    if (this.input.value.trim()) return;
    this.input.value = question;
    this.autosize();
  }

  private autosize(): void {
    const el = this.input;
    // empty: back to one line — measured now, the box would grow to fit the placeholder
    if (!el.value) {
      el.style.height = '';
      el.style.overflowY = 'hidden';
      return;
    }
    el.style.height = 'auto';
    // scrollHeight is content + padding; with box-sizing: border-box the borders have to be
    // added, or the box ends up 3px short and shows a scrollbar for a single line
    const border = el.offsetHeight - el.clientHeight;
    const wanted = el.scrollHeight + border;
    el.style.height = `${Math.min(wanted, MAX_INPUT_HEIGHT)}px`;
    el.style.overflowY = wanted > MAX_INPUT_HEIGHT ? 'auto' : 'hidden';
  }

  /** While a question is searched the input and the send button stay focusable but take nothing:
   *  read-only and `aria-disabled` — a `disabled` input would drop the focus to the page — and the
   *  placeholder says the answer is coming. The status line says that the question went out, and once
   *  more if the search takes long. */
  setBusy(busy: boolean): void {
    this.busy = busy;
    this.input.readOnly = busy;
    this.refreshTexts();
    for (const el of [this.input, this.send]) {
      if (busy) el.setAttribute('aria-disabled', 'true');
      else el.removeAttribute('aria-disabled');
    }
    clearTimeout(this.stillTimer);
    if (busy) {
      this.status.textContent = SAID_ASKED;
      this.stillTimer = setTimeout(() => (this.status.textContent = SAID_STILL), STILL_AFTER_MS);
    } else if (!this.focusIsElsewhere()) {
      // back to the form for the next question — unless the visitor went elsewhere meanwhile
      this.focusForm();
    }
  }

  /** Something outside the question form has the focus: a badge in the history, a link of the host page. */
  private focusIsElsewhere(): boolean {
    const inWidget = (this.root.getRootNode() as Document | ShadowRoot).activeElement;
    if (inWidget) return inWidget !== this.input && inWidget !== this.send;
    const onPage = this.root.ownerDocument.activeElement;
    return !!onPage && onPage !== this.root.ownerDocument.body;
  }

  setSelected(id: string | null): void {
    this.selected = id;
    this.list.querySelectorAll<HTMLElement>('.turn-assistant').forEach((el) => {
      const on = el.dataset.id === id;
      el.classList.toggle('is-selected', on);
      const badge = el.querySelector('button.map-badge');
      if (on) badge?.setAttribute('aria-current', 'true');
      else badge?.removeAttribute('aria-current');
    });
  }

  /** Bring the history in line with `turns`. A turn keeps its element while its markup stays the same —
   *  a running answer redrawn on every poll leaves the others alone, and the focus where it is. */
  render(turns: ChatTurn[]): void {
    this.turns = turns;
    const kept = new Set<string>();
    let cursor = this.list.firstElementChild;
    for (const turn of turns) {
      const html = this.turnHtml(turn);
      let entry = this.drawn.get(turn.id);
      if (entry && entry.html !== html) {
        if (cursor === entry.el) cursor = cursor.nextElementSibling;
        this.forget(turn.id);
        entry = undefined;
      }
      if (!entry) {
        entry = { html, el: elementOf(html) };
        this.drawn.set(turn.id, entry);
        this.keepInSight.observe(entry.el);
      }
      if (entry.el === cursor) cursor = cursor.nextElementSibling;
      else this.list.insertBefore(entry.el, cursor);
      kept.add(turn.id);
    }
    for (const id of [...this.drawn.keys()]) if (!kept.has(id)) this.forget(id);
    this.placeExamples();
    this.setSelected(this.selected);
    // a new question (or a cleared history): the newest turn is what to see
    const lastId = turns[turns.length - 1]?.id;
    if (lastId !== this.lastTurnId) this.anchor = { to: 'end' };
    this.lastTurnId = lastId;
    this.keepAnchor();
  }

  /** The answer `id` is complete (or failed): a long one shows its start at the top of the history —
   *  the newest turn at the bottom would show only its end — unless the visitor scrolled elsewhere
   *  meanwhile; a failed one stays at the end, with „Nochmal fragen“ whole in sight. The status line
   *  says it once. */
  answered(id: string): void {
    const el = this.drawn.get(id)?.el;
    if (!el) return;
    clearTimeout(this.stillTimer);
    const error = this.turns.find((t) => t.id === id)?.error;
    if (this.anchor) {
      this.anchor = !error && el.getBoundingClientRect().height > this.room() ? { to: 'start', id } : { to: 'end' };
      this.keepAnchor();
    }
    // the first line of the answer as the visitor sees it (heading, sentence or list)
    const first = el.querySelector('.bubble')?.firstElementChild?.textContent?.replace(/\s+/g, ' ').trim();
    this.status.textContent = error ? `Das hat nicht geklappt. ${errorText(error.kind)}` : first || 'Antwort fertig';
  }

  /** The height the history shows its turns in: its box without the padding. */
  private room(): number {
    const style = getComputedStyle(this.list);
    return this.list.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
  }

  private forget(id: string): void {
    const entry = this.drawn.get(id);
    if (!entry) return;
    this.keepInSight.unobserve(entry.el);
    entry.el.remove();
    this.drawn.delete(id);
  }

  private keepAnchor(): void {
    const anchor = this.anchor;
    if (!anchor) return;
    let top = this.list.scrollHeight;
    if (anchor.to === 'start') {
      const el = this.drawn.get(anchor.id)?.el;
      if (el) top = this.startOf(el);
      else this.anchor = { to: 'end' };
    }
    this.list.scrollTop = top;
    this.ownScrollTop = this.list.scrollTop;
    this.fadeQuestionOverError();
  }

  /** The history fades out across its top padding (styles.css, `--fade-from`/`--fade-to`). An error
   *  stays at the end with „Nochmal fragen“ whole, and the question above it may then reach that edge:
   *  cut by it, the question fades out entirely (no strip of its bubble, however tall); whole, the fade
   *  ends where it begins. Anywhere else the plain fade holds. */
  private fadeQuestionOverError(): void {
    const last = this.list.lastElementChild;
    const question = this.anchor?.to === 'end' && last?.classList.contains('is-error') ? last.previousElementSibling : null;
    const fade = question ? this.fadeOver(question) : null;
    if (fade) {
      this.list.style.setProperty('--fade-from', `${fade[0]}px`);
      this.list.style.setProperty('--fade-to', `${fade[1]}px`);
    } else {
      this.list.style.removeProperty('--fade-from');
      this.list.style.removeProperty('--fade-to');
    }
  }

  /** Where the top fade runs (px from the history's edge) so that `question` shows whole or not at
   *  all; null where the plain fade does that already (the question lies below the padding). */
  private fadeOver(question: Element): [number, number] | null {
    const style = getComputedStyle(this.list);
    const edge = this.list.getBoundingClientRect().top;
    const { top, bottom } = question.getBoundingClientRect();
    if (bottom <= edge) return null;
    if (top < edge) {
      // through the question, then across the gap below it, which shows nothing either
      const end = Math.ceil(bottom - edge);
      return [end, end + parseFloat(style.rowGap)];
    }
    if (top - edge >= parseFloat(style.paddingTop)) return null;
    const start = Math.floor(top - edge);
    return [Math.max(0, start - FADE_RAMP_PX), start];
  }

  /** Where the history scrolls to show the start of the answer `el`: with its question whole above it
   *  when the question and the first lines of the answer fit — or with the question just out of sight,
   *  never a strip of its bubble peeking over the answer from the history's padding. */
  private startOf(el: HTMLElement): number {
    const offset = (part: Element): number => this.list.scrollTop + part.getBoundingClientRect().top - this.list.getBoundingClientRect().top;
    const padding = parseFloat(getComputedStyle(this.list).paddingTop);
    const question = el.previousElementSibling;
    if (!question) return offset(el) - padding;
    const bubble = el.querySelector('.bubble') ?? el;
    const bubbleStyle = getComputedStyle(bubble);
    const firstLines = parseFloat(bubbleStyle.paddingTop) + ANSWER_LINES_UNDER_QUESTION * parseFloat(bubbleStyle.lineHeight);
    if (offset(el) - offset(question) + firstLines <= this.room()) return offset(question) - padding;
    return offset(question) + question.getBoundingClientRect().height;
  }

  private turnHtml(t: ChatTurn): string {
    if (t.role === 'user') return `<li class="turn turn-user" data-id="${t.id}"><div class="bubble">${esc(t.text)}</div></li>`;
    const a = t.answer;
    if (t.error) {
      const retry = t.error.question ? '<button type="button" class="retry">Nochmal fragen</button>' : '';
      return `<li class="turn turn-assistant is-error" data-id="${t.id}" data-detail="${esc(t.error.detail)}"><div class="bubble"><p><strong>Das hat nicht geklappt.</strong> ${esc(errorText(t.error.kind))}</p>${retry}</div></li>`;
    }
    if (!a || !a.done) {
      // A growing checklist of the real steps the travel-plan agent takes — completed calls get a
      // check, the running one a spinner. A long complex query then visibly makes progress instead
      // of one line that just says "denke nach" (Stakeholder 2026-09-11).
      const answered = new Set((a?.results ?? []).map((r) => r.callId));
      const steps = (a?.calls ?? []).slice(-8).map((c) => {
        const done = answered.has(c.callId);
        const mark = done ? '<span class="step-mark" aria-hidden="true">✓</span>' : '<span class="dots" aria-hidden="true"><i></i><i></i><i></i></span>';
        return `<li class="step ${done ? 'is-done' : 'is-active'}">${mark}<span>${esc(describeTool(c.name, c.arguments))}</span></li>`;
      });
      const head = a && a.calls.length ? (a.running.length ? 'ich sammle die Infos' : 'ich stelle die Antwort zusammen') : 'ich denke nach';
      const list = steps.length ? `<ul class="steps">${steps.join('')}</ul>` : '';
      return `<li class="turn turn-assistant is-working" data-id="${t.id}"><div class="bubble"><span class="doing"><span class="dots" aria-hidden="true"><i></i><i></i><i></i></span><span>${esc(head)} …</span></span>${list}</div></li>`;
    }
    const mappable = hasContent(t.map);
    const badge = mappable && t.map
      ? `<button type="button" class="map-badge">${MAP_ICON} ${esc(mapBadgeLabel(mapContents(t.map)))}</button>`
      : `<span class="map-badge is-empty">nichts für die Karte</span>`;
    return `<li class="turn turn-assistant ${mappable ? 'has-map' : ''}" data-id="${t.id}">
      <div class="bubble">${renderMarkdown(a.text || '(keine Antwort)')}</div>
      <div class="turn-foot">${badge}</div>
    </li>`;
  }
}

function setText(el: Element, text: string): void {
  // rewriting an unchanged text would make a screen reader read it again
  if (el.textContent !== text) el.textContent = text;
}

let measuring: CanvasRenderingContext2D | null | undefined;

/** The width of `text` in px, set in `font` (a CSS font shorthand). */
function textWidth(text: string, font: string): number {
  measuring ??= document.createElement('canvas').getContext('2d');
  if (!measuring) return Infinity;
  measuring.font = font;
  return measuring.measureText(text).width;
}

function elementOf(html: string): HTMLElement {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  return template.content.firstElementChild as HTMLElement;
}

const SEND_ICON = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M3 11.5 21 3l-8.5 18-2-7.5z" fill="currentColor"/></svg>';
const MAP_ICON = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7z" fill="currentColor"/></svg>';
